/**
 * Map LAYERS of the Agent's map card (`AgentRoutePane`) — one mechanism for every 2GIS overlay a tool sends
 * beside (or instead of) a walking line (#215, and the next ones: #216 an availability zone, #218 traffic).
 *
 * A layer rides in the transient `navigation` frame as `layers` (never in the checkpoint, INV-PH-66):
 *
 *   { layers: [ { kind: 'markers', id, title?, markers: [{ id, lat, lon, title, subtitle?, caption?, link?, number? }] },
 *               { kind: 'polygon', id, title?, rings: [[[lon, lat], …], …] } ],
 *     traffic: boolean }
 *
 * - `markers` — labelled points (places near the visitor: numbered discs with the name under them); a marker's
 *   `link` is a 2GIS page and must pass `safe2gisLink` (no key, https, 2gis host only).
 * - `polygon` — an outline (`rings[0]` outer, the rest holes; `[lon, lat]`, closed or not — it is closed here).
 * - `traffic` — the 2GIS traffic layer on the map (`map.showTraffic()`).
 *
 * `readMapLayers` validates and bounds a frame's `layers` (a malformed layer drops the whole set — a half
 * drawn overlay misleads); `drawMapLayers` puts them on a live MapGL map and returns the disposer;
 * `layersFrame` is what the camera must keep in view. The sketch fallback (no MapGL) reads the same data.
 */
import type * as MapGL from '@2gis/mapgl/types';
import { escapeHtml } from '../map/objects';
import { safe2gisLink, validCoordinate } from './navigation';
import { CLUSTER_MIN_POINTS, clusterCentre, clusterHtml, groupOnMap, zoomToCluster } from './mapClusters';
import { clipLabel, plainTitle } from './markerLabels';
import type { LabelLayout } from './markerLabelLayout';

export const MAX_LAYERS = 4;
export const MAX_LAYER_MARKERS = 20;
export const MAX_POLYGON_VERTICES = 2000;
const MAX_RINGS = 8;

export type MapLayerMarker = {
  id: string;
  lat: number;
  lon: number;
  title: string;
  subtitle?: string;
  caption?: string;
  link?: string;
  number?: number;
};
export type MarkersLayer = { kind: 'markers'; id: string; title?: string; markers: MapLayerMarker[] };
export type PolygonLayer = { kind: 'polygon'; id: string; title?: string; rings: [number, number][][] };
export type MapLayer = MarkersLayer | PolygonLayer;
export type MapLayers = { layers: MapLayer[]; traffic: boolean };

type Obj = Record<string, unknown>;
const obj = (value: unknown): value is Obj => !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown, max: number): value is string => typeof value === 'string' && value.trim().length > 0 && value.length <= max;
const optionalText = (value: unknown, max: number): boolean => value === undefined || text(value, max);
const clean = (value: string): string => value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();

function readMarker(raw: unknown): MapLayerMarker | null {
  if (!obj(raw) || !text(raw.id, 80) || !text(raw.title, 200) || !validCoordinate(raw.lat, raw.lon) ||
      !optionalText(raw.subtitle, 200) || !optionalText(raw.caption, 300) || !optionalText(raw.link, 300)) return null;
  if (raw.number !== undefined && !(Number.isInteger(raw.number) && (raw.number as number) >= 1 && (raw.number as number) <= 99)) return null;
  const marker: MapLayerMarker = { id: raw.id, lat: raw.lat as number, lon: raw.lon as number, title: clean(raw.title) };
  if (typeof raw.subtitle === 'string') marker.subtitle = clean(raw.subtitle);
  if (typeof raw.caption === 'string') marker.caption = clean(raw.caption);
  if (typeof raw.link === 'string') {
    const link = safe2gisLink(raw.link);
    if (!link) return null;
    marker.link = link;
  }
  if (typeof raw.number === 'number') marker.number = raw.number;
  return marker;
}

function readRing(raw: unknown): [number, number][] | null {
  if (!Array.isArray(raw) || raw.length < 3) return null;
  const ring: [number, number][] = [];
  for (const pair of raw) {
    if (!Array.isArray(pair) || pair.length !== 2 || !validCoordinate(pair[1], pair[0])) return null;
    ring.push([pair[0] as number, pair[1] as number]);
  }
  const [first, last] = [ring[0]!, ring.at(-1)!];
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);
  return ring.length >= 4 ? ring : null;
}

/** Validate a frame's `layers`; `null` when absent or malformed (nothing of it is drawn). */
export function readMapLayers(raw: unknown): MapLayers | null {
  if (!obj(raw) || !Array.isArray(raw.layers) || raw.layers.length > MAX_LAYERS ||
      !(raw.traffic === undefined || typeof raw.traffic === 'boolean')) return null;
  const layers: MapLayer[] = [];
  const ids = new Set<string>();
  for (const item of raw.layers) {
    if (!obj(item) || !text(item.id, 40) || ids.has(item.id) || !optionalText(item.title, 120)) return null;
    ids.add(item.id);
    const title = typeof item.title === 'string' ? { title: clean(item.title) } : {};
    if (item.kind === 'markers') {
      if (!Array.isArray(item.markers) || item.markers.length === 0 || item.markers.length > MAX_LAYER_MARKERS) return null;
      const markers = item.markers.map(readMarker);
      if (markers.some((marker) => !marker) || new Set(markers.map((marker) => marker!.id)).size !== markers.length) return null;
      layers.push({ kind: 'markers', id: item.id, ...title, markers: markers as MapLayerMarker[] });
    } else if (item.kind === 'polygon') {
      if (!Array.isArray(item.rings) || item.rings.length === 0 || item.rings.length > MAX_RINGS) return null;
      const rings = item.rings.map(readRing);
      if (rings.some((ring) => !ring) || rings.reduce((sum, ring) => sum + ring!.length, 0) > MAX_POLYGON_VERTICES) return null;
      layers.push({ kind: 'polygon', id: item.id, ...title, rings: rings as [number, number][][] });
    } else {
      return null;
    }
  }
  const traffic = raw.traffic === true;
  return layers.length || traffic ? { layers, traffic } : null;
}

/** The markers of the first markers layer — what a card lists under its map. */
export function listedMarkers(layers: MapLayers | undefined): MarkersLayer | null {
  return (layers?.layers.find((layer) => layer.kind === 'markers') as MarkersLayer | undefined) ?? null;
}

/** Every `[lon, lat]` the camera has to keep in view. */
export function layersFrame(layers: MapLayers | undefined): [number, number][] {
  const out: [number, number][] = [];
  for (const layer of layers?.layers ?? []) {
    if (layer.kind === 'markers') out.push(...layer.markers.map((marker) => [marker.lon, marker.lat] as [number, number]));
    else out.push(...layer.rings[0]!);
  }
  return out;
}

/** A marker's disc: its number (or «•») with the name beside it, in the chat's picture too — HTML, every text escaped.
 *  The name is laid out by `markerLabelLayout.ts`: hidden where it would lie on another, whole in `title`. */
export function layerMarkerHtml(marker: MapLayerMarker): string {
  const title = plainTitle(marker.title);
  return `<span class="map-layer-marker" title="${escapeHtml(title)}"><span class="map-layer-marker__disc">${marker.number ?? '•'}</span><span class="agent-marker-label">${escapeHtml(clipLabel(title))}</span></span>`;
}

const DISC_PX = 24;

type Padding = Parameters<typeof zoomToCluster>[2];

/**
 * Draw *layers* on a live map; returns the disposer (markers, groups, polygons, the traffic layer, the zoom
 * handlers). Full screen (`inline=false`) a marker with a link opens its 2GIS page in a new tab; in the chat
 * the map is a picture and takes no taps. A markers layer of `CLUSTER_MIN_POINTS` or more is grouped by
 * screen distance as the map's own points are (#220, INV-PH-97): a tap on a group zooms to its points. The
 * outline and the visitor are never grouped; the card's list keeps every marker and its number.
 */
export function drawMapLayers(sdk: typeof MapGL, map: MapGL.Map, layers: MapLayers | undefined, inline: boolean,
  colors: { fill: string; stroke: string } = { fill: '#1769aa22', stroke: '#1769aa' },
  padding: Padding = { top: 40, right: 40, bottom: 40, left: 40 }, labels?: LabelLayout | null): () => void {
  const objects: { destroy: () => void }[] = [];
  const disposers: (() => void)[] = [];
  const drawOne = (item: MapLayerMarker, index: number) => {
    const marker = new sdk.HtmlMarker(map, {
      coordinates: [item.lon, item.lat],
      html: layerMarkerHtml(item),
      anchor: [DISC_PX / 2, DISC_PX / 2],
      zIndex: 50 - index,
      preventMapInteractions: !inline && !!item.link,
    });
    if (!inline && item.link) {
      const element = marker.getContent();
      element.style.cursor = 'pointer';
      const link = item.link;
      element.addEventListener('click', (event) => { event.stopPropagation(); window.open(link, '_blank', 'noopener,noreferrer'); });
    }
    labels?.add(marker.getContent(), 10 + index);
    return marker;
  };
  for (const layer of layers?.layers ?? []) {
    if (layer.kind === 'polygon') {
      objects.push(new sdk.Polygon(map, { coordinates: layer.rings, color: colors.fill, strokeColor: colors.stroke, strokeWidth: 2, zIndex: 10 }));
      continue;
    }
    if (layer.markers.length < CLUSTER_MIN_POINTS) {
      layer.markers.forEach((item, index) => objects.push(drawOne(item, index)));
      continue;
    }
    let drawn: MapGL.HtmlMarker[] = [];
    let signature = '';
    const regroup = () => {
      const groups = groupOnMap(map, layer.markers);
      const next = groups.map((group) => group.map((member) => layer.markers.indexOf(member)).join(',')).join('|');
      if (next === signature) return;
      signature = next;
      drawn.forEach((item) => { labels?.remove(item.getContent()); item.destroy(); });
      drawn = groups.map((group) => {
        if (group.length === 1) return drawOne(group[0]!, layer.markers.indexOf(group[0]!));
        const [lon, lat] = clusterCentre(group);
        const cluster = new sdk.HtmlMarker(map, { coordinates: [lon, lat], html: clusterHtml(group.length), anchor: [17, 17], zIndex: 45, preventMapInteractions: true });
        cluster.getContent().addEventListener('click', (event) => { event.stopPropagation(); zoomToCluster(map, group, padding); });
        return cluster;
      });
      labels?.refresh();
    };
    regroup();
    map.on('zoomend', regroup);
    map.on('idle', regroup);
    disposers.push(() => { map.off('zoomend', regroup); map.off('idle', regroup); drawn.forEach((item) => { labels?.remove(item.getContent()); item.destroy(); }); drawn = []; });
  }
  if (layers?.traffic) map.showTraffic();
  return () => {
    objects.forEach((item) => item.destroy());
    disposers.forEach((dispose) => dispose());
    if (layers?.traffic) map.hideTraffic();
  };
}
